import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, Inbox, Users, Megaphone, MoreHorizontal, X, Building2, BookOpen, FileBarChart, TrendingUp, Filter, Settings } from 'lucide-react'
import { get } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'

const PRIMARY = [
  { to: '/',          icon: BarChart3,  label: 'Dashboard' },
  { to: '/cases',     icon: Inbox,      label: 'Cases',      counter: 'openCases' as const },
  { to: '/contacts',  icon: Users,      label: 'Contacts' },
  { to: '/campaigns', icon: Megaphone,  label: 'Campaigns' },
]

const OVERFLOW = [
  { to: '/accounts',       icon: Building2,   label: 'Accounts' },
  { to: '/whatsapp/dashboard', icon: BarChart3, label: 'WhatsApp Dashboard', roles: ['TENANT_ADMIN', 'SUPER_ADMIN'] as string[] },
  { to: '/leads',          icon: Filter,      label: 'Leads' },
  { to: '/opportunities',  icon: TrendingUp,  label: 'Opportunities' },
  { to: '/kb',             icon: BookOpen,    label: 'Knowledge Base' },
  { to: '/reports',        icon: FileBarChart,label: 'Reports' },
  { to: '/settings',       icon: Settings,    label: 'Settings', roles: ['TENANT_ADMIN', 'SUPER_ADMIN'] as string[] },
]

/**
 * Bottom tab bar shown only at <768px. Replaces the desktop sidebar; a "More"
 * tab opens a slide-up sheet with the remaining nav items.
 */
export function MobileTabBar() {
  const [sheetOpen, setSheetOpen] = useState(false)
  const role = useAuthStore((s) => s.user?.role)

  const { data: usage } = useQuery({
    queryKey: ['tenant', 'usage'],
    queryFn: () => get<{ openCases: number }>('/api/v1/tenant/usage'),
    staleTime: 60_000,
  })

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 bg-card border-t border-border z-40 h-14 flex items-stretch"
        aria-label="Primary mobile navigation"
      >
        {PRIMARY.map((t) => {
          const Icon = t.icon
          const badge = t.counter === 'openCases' && usage?.openCases ? usage.openCases : null
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px]',
                  isActive ? 'text-green' : 'text-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <Icon className="h-5 w-5" />
                    {badge ? (
                      <span className="absolute -top-1 -right-2 bg-green text-white text-[9px] font-bold rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">{badge}</span>
                    ) : null}
                    {isActive ? <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-0.5 w-8 bg-green rounded-full" /> : null}
                  </span>
                  <span>{t.label}</span>
                </>
              )}
            </NavLink>
          )
        })}
        <button
          onClick={() => setSheetOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] text-muted"
          aria-label="More navigation"
        >
          <MoreHorizontal className="h-5 w-5" />
          <span>More</span>
        </button>
      </nav>

      {sheetOpen ? (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-label="More navigation">
          <div className="absolute inset-0 bg-heading/40" onClick={() => setSheetOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-card rounded-t-card shadow-elevated p-4 animate-[slideUp_0.15s_ease-out] safe-area-inset-bottom">
            <div className="flex items-center justify-between mb-3">
              <div className="label-xs">More</div>
              <button onClick={() => setSheetOpen(false)} aria-label="Close"><X className="h-4 w-4 text-muted" /></button>
            </div>
            <ul className="grid grid-cols-2 gap-2">
              {OVERFLOW.filter((i) => !i.roles || (role && i.roles.includes(role))).map((i) => {
                const Icon = i.icon
                return (
                  <li key={i.to}>
                    <NavLink
                      to={i.to}
                      onClick={() => setSheetOpen(false)}
                      className={({ isActive }) => cn(
                        'flex items-center gap-3 px-3 py-3 rounded-input text-sm',
                        isActive ? 'bg-violet-tint text-violet' : 'bg-page text-body',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{i.label}</span>
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  )
}
